import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Eye, EyeOff, Shield, AlertCircle, Loader2, MessageSquare, RefreshCw } from 'lucide-react';
import { logAdminAction, AUDIT_ACTIONS } from '../../services/auditLogService';

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;

async function callSendOtp({ action, phone, email, otp, purpose }) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/send-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ action, phone, email, otp, purpose }),
  });
  return res?.json();
}

const inputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
};

const AdminLoginScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if redirected due to inactivity
  const inactivityLogout = location?.state?.reason === 'inactivity';
  // Check if redirected after successful password reset
  const passwordResetSuccess = location?.state?.message;

  // ── Login state ──────────────────────────────────────────────────────────
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── OTP state (login) ────────────────────────────────────────────────────
  const [showOtp, setShowOtp] = useState(false);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpResending, setOtpResending] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [pendingSession, setPendingSession] = useState(null); // holds session after password auth
  const otpRefs = useRef([]);

  // ── Forgot password state ────────────────────────────────────────────────
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [showForgotOtp, setShowForgotOtp] = useState(false);
  const [forgotOtpDigits, setForgotOtpDigits] = useState(['', '', '', '', '', '']);
  const [forgotOtpLoading, setForgotOtpLoading] = useState(false);
  const [forgotOtpCountdown, setForgotOtpCountdown] = useState(0);
  const forgotOtpRefs = useRef([]);

  // Redirect if already authenticated
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session) navigate('/admin-dashboard', { replace: true });
    };
    checkSession();
  }, [navigate]);

  // Countdown timer helper
  useEffect(() => {
    if (otpCountdown <= 0) return;
    const t = setTimeout(() => setOtpCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCountdown]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const getAdminPhone = async (userEmail) => {
    const { data } = await supabase?.from('user_profiles')?.select('phone')?.eq('id', (await supabase?.auth?.getUser())?.data?.user?.id)?.single();
    return data?.phone || null;
  };

  const handleOtpDigitChange = (digits, setDigits, refs, index, value) => {
    if (!/^\d*$/?.test(value)) return;
    const next = [...digits];
    next[index] = value?.slice(-1);
    setDigits(next);
    if (value && index < 5) refs?.current?.[index + 1]?.focus();
  };

  const handleOtpKeyDown = (digits, setDigits, refs, index, e) => {
    if (e?.key === 'Backspace' && !digits?.[index] && index > 0) {
      refs?.current?.[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (setDigits, refs, e) => {
    const pasted = e?.clipboardData?.getData('text')?.replace(/\D/g, '')?.slice(0, 6);
    if (pasted?.length === 6) {
      setDigits(pasted?.split(''));
      refs?.current?.[5]?.focus();
    }
  };

  // ── Sign In (Step 1: password auth) ──────────────────────────────────────
  const handleSignIn = async (e) => {
    e?.preventDefault();
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    setLoading(true);
    setError('');
    try {
      const { data, error: signInError } = await supabase?.auth?.signInWithPassword({ email, password });
      if (signInError) {
        setError(signInError?.message || 'Invalid credentials.');
        // Log failed login attempt (no session yet, use anonymous insert via service role not available client-side — skip)
        return;
      }

      if (data?.session) {
        // Fetch phone for OTP
        const { data: profile } = await supabase?.from('user_profiles')?.select('phone, full_name')?.eq('id', data?.session?.user?.id)?.single();

        const phone = profile?.phone;
        if (!phone) {
          // No phone on file — skip OTP, log login, go straight to dashboard
          await logAdminAction({
            actionType: AUDIT_ACTIONS?.LOGIN,
            actorName: profile?.full_name || data?.session?.user?.email?.split('@')?.[0] || 'Admin',
            notes: `Admin login from ${data?.session?.user?.email}`,
          });
          navigate('/admin-dashboard', { replace: true });
          return;
        }

        // Sign out temporarily — only let through after OTP
        await supabase?.auth?.signOut();
        setPendingSession({ email, password, phone, fullName: profile?.full_name });

        // Send OTP
        const result = await callSendOtp({ action: 'send', phone, email, purpose: 'login' });
        if (result?.error) { setError(result?.error); return; }

        setShowOtp(true);
        setOtpCountdown(60);
        setTimeout(() => otpRefs?.current?.[0]?.focus(), 100);
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // ── OTP Verify (login) ────────────────────────────────────────────────────
  const handleVerifyLoginOtp = async (e) => {
    e?.preventDefault();
    const otp = otpDigits?.join('');
    if (otp?.length < 6) { setError('Please enter the 6-digit code.'); return; }
    setOtpLoading(true);
    setError('');
    try {
      const result = await callSendOtp({ action: 'verify', email: pendingSession?.email, otp, purpose: 'login' });
      if (result?.error) { setError(result?.error); return; }

      // OTP verified — re-authenticate and navigate
      const { data, error: signInError } = await supabase?.auth?.signInWithPassword({
        email: pendingSession?.email,
        password: pendingSession?.password,
      });
      if (signInError) { setError('Authentication failed. Please start over.'); return; }
      if (data?.session) {
        await logAdminAction({
          actionType: AUDIT_ACTIONS?.LOGIN,
          actorName: pendingSession?.fullName || pendingSession?.email?.split('@')?.[0] || 'Admin',
          notes: `Admin login (OTP verified) from ${pendingSession?.email}`,
        });
        navigate('/admin-dashboard', { replace: true });
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Resend OTP (login) ────────────────────────────────────────────────────
  const handleResendLoginOtp = async () => {
    if (otpCountdown > 0 || !pendingSession) return;
    setOtpResending(true);
    setError('');
    try {
      const result = await callSendOtp({
        action: 'send',
        phone: pendingSession?.phone,
        email: pendingSession?.email,
        purpose: 'login',
      });
      if (result?.error) { setError(result?.error); return; }
      setOtpDigits(['', '', '', '', '', '']);
      setOtpCountdown(60);
      setTimeout(() => otpRefs?.current?.[0]?.focus(), 100);
    } catch (err) {
      setError('Failed to resend code.');
    } finally {
      setOtpResending(false);
    }
  };

  // ── Forgot Password (Step 1: send OTP) ───────────────────────────────────
  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    if (!forgotEmail) { setError('Please enter your email address.'); return; }
    setForgotLoading(true);
    setError('');
    try {
      const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(forgotEmail, {
        redirectTo: `https://onthegoglobal.co.ke/admin-dashboard`,
      });
      if (resetError) { setError(resetError?.message || 'Failed to send reset email.'); return; }
      setForgotSent(true);
    } catch (err) {
      setError('Failed to send reset email. Please check your connection and try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyForgotOtp = async (e) => {
    e?.preventDefault();
    const otp = forgotOtpDigits?.join('');
    if (otp?.length < 6) { setError('Please enter the 6-digit code.'); return; }
    setForgotOtpLoading(true);
    setError('');
    try {
      const result = await callSendOtp({ action: 'verify', email: forgotEmail, otp, purpose: 'reset' });
      if (result?.error) { setError(result?.error); return; }
      setForgotSent(true);
      setShowForgotOtp(false);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setForgotOtpLoading(false);
    }
  };

  const handleResendForgotOtp = async () => {
    if (forgotOtpCountdown > 0) return;
    setError('');
    try {
      const result = await callSendOtp({ action: 'send', email: forgotEmail, purpose: 'reset' });
      if (result?.error) { setError(result?.error); return; }
      setForgotOtpDigits(['', '', '', '', '', '']);
      setForgotOtpCountdown(60);
      setTimeout(() => forgotOtpRefs?.current?.[0]?.focus(), 100);
    } catch (err) {
      setError('Failed to resend code.');
    }
  };

  // ── OTP Input Component ───────────────────────────────────────────────────
  const OtpInputRow = ({ digits, setDigits, refs, onPaste }) => (
    <div className="flex gap-2 justify-center">
      {digits?.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={(e) => handleOtpDigitChange(digits, setDigits, refs, i, e?.target?.value)}
          onKeyDown={(e) => handleOtpKeyDown(digits, setDigits, refs, i, e)}
          onPaste={onPaste}
          className="w-11 h-12 text-center text-white text-lg font-bold rounded-xl outline-none transition-all"
          style={inputStyle}
          onFocus={(e) => { e.target.style.borderColor = '#ffd700'; e.target.style.boxShadow = '0 0 0 2px rgba(255,215,0,0.2)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
        />
      ))}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #060e1e 0%, #0a1628 50%, #0d1f3c 100%)' }}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #ffd700, transparent)' }} />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #ffd700, transparent)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-3" style={{ background: 'radial-gradient(circle, #0a1628, transparent)' }} />
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(255,215,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,0,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>
      <div className="relative w-full max-w-md mx-auto px-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: 'linear-gradient(135deg, #ffd700, #e6c200)' }}>
            <svg viewBox="0 0 24 24" className="w-9 h-9" fill="#0a1628">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#0a1628" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight">
            On<span style={{ color: '#ffd700' }}>TheGo</span>
          </h1>
          <p className="text-white/40 text-sm mt-1 tracking-widest uppercase">Admin Portal</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-white/10 p-8" style={{ background: 'rgba(13, 31, 60, 0.8)', backdropFilter: 'blur(20px)' }}>

          {/* Inactivity notice */}
          {inactivityLogout && !showOtp && !showForgot && (
            <div className="flex items-start gap-3 p-3 rounded-xl mb-5 border" style={{ background: 'rgba(255,193,7,0.1)', borderColor: 'rgba(255,193,7,0.3)' }}>
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#ffd700' }} />
              <p className="text-sm" style={{ color: '#ffd700' }}>
                You were logged out due to 15 minutes of inactivity. Please sign in again.
              </p>
            </div>
          )}

          {/* Password reset success notice */}
          {passwordResetSuccess && !showOtp && !showForgot && !inactivityLogout && (
            <div className="flex items-start gap-3 p-3 rounded-xl mb-5 border" style={{ background: 'rgba(34,197,94,0.1)', borderColor: 'rgba(34,197,94,0.3)' }}>
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-400" />
              <p className="text-sm text-green-400">{passwordResetSuccess}</p>
            </div>
          )}

          {/* ── LOGIN OTP STEP ── */}
          {showOtp && !showForgot ? (
            <>
              <div className="mb-6 text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(255,215,0,0.15)' }}>
                  <MessageSquare className="w-6 h-6" style={{ color: '#ffd700' }} />
                </div>
                <h2 className="text-xl font-semibold text-white">Verify Your Identity</h2>
                <p className="text-white/40 text-sm mt-1">
                  Enter the 6-digit code sent to your registered phone number
                </p>
              </div>
              {error && (
                <div className="flex items-start gap-3 p-3 rounded-xl mb-5 border" style={{ background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.3)' }}>
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}
              <form onSubmit={handleVerifyLoginOtp} className="space-y-6">
                <OtpInputRow
                  digits={otpDigits}
                  setDigits={setOtpDigits}
                  refs={otpRefs}
                  onPaste={(e) => handleOtpPaste(setOtpDigits, otpRefs, e)}
                />

                <button
                  type="submit"
                  disabled={otpLoading || otpDigits?.join('')?.length < 6}
                  className="w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, #ffd700, #e6c200)', color: '#0a1628' }}
                >
                  {otpLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</> : 'Verify & Sign In'}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendLoginOtp}
                    disabled={otpCountdown > 0 || otpResending}
                    className="text-sm flex items-center gap-1.5 mx-auto transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ color: otpCountdown > 0 ? 'rgba(255,255,255,0.3)' : '#ffd700' }}
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${otpResending ? 'animate-spin' : ''}`} />
                    {otpCountdown > 0 ? `Resend in ${otpCountdown}s` : 'Resend Code'}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => { setShowOtp(false); setPendingSession(null); setOtpDigits(['','','','','','']); setError(''); }}
                  className="w-full text-white/40 hover:text-white/60 text-sm transition-colors"
                >
                  ← Back to Sign In
                </button>
              </form>
            </>

          /* ── FORGOT PASSWORD OTP STEP ── */
          ) : showForgotOtp ? (
            <>
              <div className="mb-6">
                <button
                  onClick={() => { setShowForgotOtp(false); setError(''); setForgotOtpDigits(['','','','','','']); }}
                  className="text-white/40 hover:text-white/70 text-sm flex items-center gap-1 mb-4 transition-colors"
                >
                  ← Back
                </button>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(255,215,0,0.15)' }}>
                    <MessageSquare className="w-6 h-6" style={{ color: '#ffd700' }} />
                  </div>
                  <h2 className="text-xl font-semibold text-white">Verify Reset Request</h2>
                  <p className="text-white/40 text-sm mt-1">Enter the 6-digit code sent to your phone</p>
                </div>
              </div>
              {error && (
                <div className="flex items-start gap-3 p-3 rounded-xl mb-5 border" style={{ background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.3)' }}>
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}
              <form onSubmit={handleVerifyForgotOtp} className="space-y-6">
                <OtpInputRow
                  digits={forgotOtpDigits}
                  setDigits={setForgotOtpDigits}
                  refs={forgotOtpRefs}
                  onPaste={(e) => handleOtpPaste(setForgotOtpDigits, forgotOtpRefs, e)}
                />

                <button
                  type="submit"
                  disabled={forgotOtpLoading || forgotOtpDigits?.join('')?.length < 6}
                  className="w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, #ffd700, #e6c200)', color: '#0a1628' }}
                >
                  {forgotOtpLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</> : 'Verify Code'}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendForgotOtp}
                    disabled={forgotOtpCountdown > 0}
                    className="text-sm flex items-center gap-1.5 mx-auto transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ color: forgotOtpCountdown > 0 ? 'rgba(255,255,255,0.3)' : '#ffd700' }}
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    {forgotOtpCountdown > 0 ? `Resend in ${forgotOtpCountdown}s` : 'Resend Code'}
                  </button>
                </div>
              </form>
            </>

          /* ── FORGOT PASSWORD EMAIL STEP ── */
          ) : showForgot ? (
            <>
              <div className="mb-6">
                <button
                  onClick={() => { setShowForgot(false); setError(''); setForgotSent(false); setShowForgotOtp(false); }}
                  className="text-white/40 hover:text-white/70 text-sm flex items-center gap-1 mb-4 transition-colors"
                >
                  ← Back to Sign In
                </button>
                <h2 className="text-xl font-semibold text-white">Reset Password</h2>
                <p className="text-white/40 text-sm mt-1">Enter your email to receive a reset link & SMS code</p>
              </div>
              {error && (
                <div className="flex items-start gap-3 p-3 rounded-xl mb-5 border" style={{ background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.3)' }}>
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}
              {forgotSent ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(255,215,0,0.15)' }}>
                    <Shield className="w-6 h-6" style={{ color: '#ffd700' }} />
                  </div>
                  <p className="text-white font-medium">Identity verified!</p>
                  <p className="text-white/40 text-sm mt-1">Check your email inbox for the password reset link.</p>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div>
                    <label className="block text-white/60 text-sm font-medium mb-2">Email Address</label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e?.target?.value)}
                      placeholder="admin@onthego.co.ke"
                      className="w-full px-4 py-3 rounded-xl text-white placeholder-white/20 text-sm outline-none transition-all"
                      style={inputStyle}
                      onFocus={(e) => { e.target.style.borderColor = '#ffd700'; e.target.style.boxShadow = '0 0 0 2px rgba(255,215,0,0.15)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                      disabled={forgotLoading}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ background: 'linear-gradient(135deg, #ffd700, #e6c200)', color: '#0a1628' }}
                  >
                    {forgotLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : 'Send Reset Link & SMS Code'}
                  </button>
                </form>
              )}
            </>

          /* ── MAIN LOGIN STEP ── */
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-white">Sign In</h2>
                <p className="text-white/40 text-sm mt-1">Access the administration dashboard</p>
              </div>

              {error && (
                <div className="flex items-start gap-3 p-3 rounded-xl mb-5 border" style={{ background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.3)' }}>
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSignIn} className="space-y-5">
                <div>
                  <label className="block text-white/60 text-sm font-medium mb-2">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    placeholder="admin@onthego.co.ke"
                    autoComplete="email"
                    className="w-full px-4 py-3 rounded-xl text-white placeholder-white/20 text-sm outline-none transition-all"
                    style={inputStyle}
                    onFocus={(e) => { e.target.style.borderColor = '#ffd700'; e.target.style.boxShadow = '0 0 0 2px rgba(255,215,0,0.15)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                    disabled={loading}
                    aria-label="Email address"
                  />
                </div>

                <div>
                  <label className="block text-white/60 text-sm font-medium mb-2">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e?.target?.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      className="w-full px-4 py-3 pr-12 rounded-xl text-white placeholder-white/20 text-sm outline-none transition-all"
                      style={inputStyle}
                      onFocus={(e) => { e.target.style.borderColor = '#ffd700'; e.target.style.boxShadow = '0 0 0 2px rgba(255,215,0,0.15)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
                      disabled={loading}
                      aria-label="Password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e?.target?.checked)}
                      className="w-4 h-4 rounded accent-yellow-400"
                    />
                    <span className="text-white/50 text-sm">Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => { setShowForgot(true); setError(''); setForgotEmail(email); }}
                    className="text-sm transition-colors"
                    style={{ color: '#ffd700' }}
                    onMouseEnter={(e) => e.target.style.color = '#e6c200'}
                    onMouseLeave={(e) => e.target.style.color = '#ffd700'}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: loading ? 'rgba(255,215,0,0.6)' : 'linear-gradient(135deg, #ffd700, #e6c200)', color: '#0a1628' }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</>
                  ) : (
                    'Sign In to Dashboard'
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Security notice */}
        <div className="flex items-center justify-center gap-2 mt-6">
          <Shield className="w-3.5 h-3.5 text-white/20" />
          <p className="text-white/20 text-xs">Authorized personnel only · All access is logged and monitored</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginScreen;
